/**
 * AuthContext.jsx
 *
 * Global authentication context for the Angle! app.
 *
 * Provides:
 *   - user            – Supabase auth user object (null when signed out)
 *   - userProfile     – Row from `user_profiles` table
 *   - creditBalance   – Current credit balance from `credits` table
 *   - analysisHistory – Last 5 analysis sessions from `analysis_history`
 *   - loading         – True while the initial session check is in progress
 *   - profileLoading  – True while profile/credits/history are being fetched
 *   - isAuthenticated – Convenience boolean derived from `user`
 *   - signUp / signIn / signOut / updateProfile / refreshCredits / refreshAnalysisHistory
 *
 * Usage:
 *   Wrap your app in <AuthProvider> (done in Routes.jsx).
 *   Access values with the `useAuth()` hook from any child component.
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

// Create the context with an empty default so TypeScript/IDE tooling works
const AuthContext = createContext({});

/**
 * Custom hook to consume the AuthContext.
 * Throws if used outside of <AuthProvider>.
 *
 * @returns {object} Auth context value
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

/**
 * AuthProvider
 *
 * Wraps the application and keeps auth state in sync with Supabase.
 * On mount it reads the existing session; thereafter it reacts to
 * `onAuthStateChange` events (sign-in, sign-out, token refresh).
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [creditBalance, setCreditBalance] = useState(0);
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);

  // ─── Profile operations (grouped to avoid repetition) ──────────────────────

  /**
   * Loads the user profile, credit balance, and recent analysis history
   * for the given userId. All three queries run in parallel.
   *
   * @param {string} userId - Supabase auth user UUID
   */
  const loadProfile = async (userId) => {
    if (!userId) return;
    setProfileLoading(true);

    try {
      // Run all three queries concurrently for faster page load
      const [profileResult, creditResult, historyResult] = await Promise.all([
        supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.maybeSingle(),
        supabase?.from('credits')?.select('balance')?.eq('user_id', userId)?.maybeSingle(),
        supabase?.from('analysis_history')?.select('id, photo_count, average_score, highest_score, gender_filter, credits_used, created_at, results')?.eq('user_id', userId)?.order('created_at', { ascending: false })?.limit(5),
      ]);

      if (!profileResult?.error && profileResult?.data) setUserProfile(profileResult?.data);
      if (!creditResult?.error && creditResult?.data) setCreditBalance(creditResult?.data?.balance ?? 0);
      if (!historyResult?.error && historyResult?.data) setAnalysisHistory(historyResult?.data);
    } catch (error) {
      console.error('Profile load error:', error);
    } finally {
      setProfileLoading(false);
    }
  };

  /**
   * Clears all profile-related state (called on sign-out).
   */
  const clearProfile = () => {
    setUserProfile(null);
    setCreditBalance(0);
    setAnalysisHistory([]);
    setProfileLoading(false);
  };

  // ─── Auth state listener ──────────────────────────────────────────────────

  useEffect(() => {
    /**
     * Handles auth state changes (sign-in, sign-out, token refresh).
     * Triggered both by the initial getSession call and by onAuthStateChange.
     */
    const handleAuthChange = (event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);

      if (session?.user) {
        loadProfile(session?.user?.id);
      } else {
        clearProfile();
      }
    };

    // Hydrate state from the existing session on mount
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      handleAuthChange(null, session);
    });

    // Subscribe to future auth events
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(handleAuthChange);

    // Unsubscribe when the provider unmounts
    return () => subscription?.unsubscribe();
  }, []);

  // ─── Auth actions ─────────────────────────────────────────────────────────

  /**
   * Creates a new Supabase account and sends a confirmation email.
   *
   * @param {string} email
   * @param {string} password
   * @param {string} [fullName]
   */
  const signUp = async (email, password, fullName) => {
    try {
      return await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: { full_name: fullName || '' },
          emailRedirectTo: `${window.location?.origin}/confirm-email`,
        },
      });
    } catch {
      return { error: { message: 'Network error. Please try again.' } };
    }
  };

  /**
   * Signs in an existing user with email and password.
   *
   * @param {string} email
   * @param {string} password
   */
  const signIn = async (email, password) => {
    try {
      return await supabase?.auth?.signInWithPassword({ email, password });
    } catch {
      return { error: { message: 'Network error. Please try again.' } };
    }
  };

  /**
   * Signs out the current user and clears local state.
   */
  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut();
      if (!error) {
        setUser(null);
        clearProfile();
      }
      return { error };
    } catch {
      return { error: { message: 'Network error. Please try again.' } };
    }
  };

  /**
   * Updates the authenticated user's profile row in `user_profiles`.
   *
   * @param {object} updates - Partial profile fields to update
   */
  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } };
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.update(updates)
        ?.eq('id', user?.id)
        ?.select()
        ?.maybeSingle();

      if (!error && data) setUserProfile(data);
      return { data, error };
    } catch {
      return { error: { message: 'Network error. Please try again.' } };
    }
  };

  /**
   * Re-fetches the credit balance for the current user.
   * Call this after a purchase or after an analysis consumes a credit.
   */
  const refreshCredits = async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        ?.from('credits')
        ?.select('balance')
        ?.eq('user_id', user?.id)
        ?.maybeSingle();

      if (!error) setCreditBalance(data?.balance ?? 0);
    } catch (error) {
      console.error('Credit refresh error:', error);
    }
  };

  /**
   * Keeps credit balance synchronized across the app in near real-time.
   * This picks up webhook-delivered credits without requiring manual refresh.
   */
  useEffect(() => {
    if (!user?.id) return undefined;

    const channel = supabase?.channel(`credits-balance-${user?.id}`)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'credits',
          filter: `user_id=eq.${user?.id}`,
        },
        (payload) => {
          const nextBalance = payload?.new?.balance;
          if (typeof nextBalance === 'number') {
            setCreditBalance(nextBalance);
            return;
          }

          // Fallback in case payload is partial.
          refreshCredits?.();
        }
      )?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  }, [user?.id]);

  /**
   * Re-fetches the last 5 analysis sessions for the current user.
   * Call this after a new analysis completes.
   */
  const refreshAnalysisHistory = async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        ?.from('analysis_history')
        ?.select('id, photo_count, average_score, highest_score, gender_filter, credits_used, created_at, results')
        ?.eq('user_id', user?.id)
        ?.order('created_at', { ascending: false })
        ?.limit(5);

      if (!error && data) setAnalysisHistory(data);
    } catch (error) {
      console.error('Analysis history refresh error:', error);
    }
  };

  // ─── Context value ────────────────────────────────────────────────────────

  const value = {
    user,
    userProfile,
    creditBalance,
    analysisHistory,
    loading,
    profileLoading,
    signUp,
    signIn,
    signOut,
    updateProfile,
    refreshCredits,
    refreshAnalysisHistory,
    /** Convenience boolean — true when a user session is active */
    isAuthenticated: !!user,
    /**
     * Display-ready name derived from the user profile.
     * Computed once here so every consumer gets the same value without
     * duplicating the derivation logic.
     */
    displayName:
      userProfile?.full_name ||
      user?.user_metadata?.full_name ||
      user?.email?.split('@')?.[0] ||
      'User',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
